import React, { useState } from 'react';
import { Drawer, Table, Button, Input, Icon } from 'antd';
import './TestsDrawer.less';
import { getConfig, setConfig } from 'utils/config';
import { useQuery } from 'api-hooks/hooks';
import { getTests } from '@api/testsApi';
import { Test } from '@models/test';

type Props = {
  visible: boolean;
  formName: string;
  notesText: string;
  onClose: (id?: number) => any;
  selectedRowKeys: number[];
  onSelect: (test: Test) => any;
  onDeselect: (id: number) => any;
};

const TestsDrawer: React.FC<Props> = ({
  visible,
  formName,
  notesText,
  onClose,
  selectedRowKeys,
  onSelect,
  onDeselect
}) => {
  const [code, setCode] = useState();
  const [name, setName] = useState();
  const [page, setPage] = useState({ current: 1, size: getConfig('pageSize') });
  const { data: tests, loading } = useQuery(
    getTests(page.current, page.size, {
      name__icontains: name,
      test_code__startswith: code
    })
  );

  const getColumnSearchProps = dataIndex => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={e =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Button
          type="primary"
          onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          icon="search"
          size="small"
          style={{ width: 90, marginRight: 8 }}>
          Search
        </Button>
        <Button
          onClick={() => handleReset(dataIndex, clearFilters)}
          size="small"
          style={{ width: 90 }}>
          Reset
        </Button>
      </div>
    )
  });

  const columns = [
    {
      title: 'Test Code',
      dataIndex: 'test_code',
      ...getColumnSearchProps('code')
    },
    { title: 'Test Name', dataIndex: 'name', ...getColumnSearchProps('name') }
  ];

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    if (dataIndex === 'code') {
      setCode(selectedKeys[0]);
    } else if (dataIndex === 'name') {
      setName(selectedKeys[0]);
    }
    confirm();
  };

  const checkRecord = (record, selectedRowKeys) => {
    let limit = formName.startsWith("Equine")? 6 : 17;
    let noteLines;
    if (notesText && notesText.split(/\n/).length > 0) {
      noteLines = notesText.split(/\n/).length
    } else {
      noteLines = Math.round(notesText.length/66);
    }
    limit -= noteLines;
    const selected = selectedRowKeys.includes(record.id);
    return !selected && !(selectedRowKeys.length < limit );
  }

  const handleReset = (dataIndex, confirm) => {
    if (dataIndex === 'code') {
      setCode(undefined);
    } else if (dataIndex === 'name') {
      setName(undefined);
    }
    confirm();
  };

  const handleTableChange = (pagination, filters, sorter) => {
    setPage({ current: pagination.current, size: pagination.pageSize });
    setConfig('pageSize', pagination.pageSize);
  };

  const rowSelection = {
    selectedRowKeys: selectedRowKeys,
    onChange: (newRowKeys: any[], newRows: Test[]) => {
      const numberKeys = newRowKeys.map(x => parseInt(x, 10));
      const added = numberKeys.filter(x => !selectedRowKeys.includes(x));
      const removed = selectedRowKeys.filter(x => !numberKeys.includes(x));
      added.forEach(k => onSelect(newRows.find(x => x.id === k) as Test));
      removed.forEach(onDeselect);
    },
    getCheckboxProps: record => ({
      disabled: checkRecord(record, selectedRowKeys),
    }),
  };

  return (
    <Drawer
      width={1300}
      className="TestsDrawer-drawer"
      title={
        <div className="Customer-List-header">
          <div><b>Selecting Test Codes.</b></div>
          <div>
            Click on the funnel icon&nbsp;
            <Icon type="filter" theme="filled" />
            &nbsp;and to search.
          </div>
          <div>
            Note: Selection may be disabled due to the printing space restrictions on the form. 
          </div>
        </div>
      }
      visible={visible}
      onClose={() => onClose()}
      closable={false}>
      <Table
        bordered={true}
        columns={columns}
        dataSource={tests?.results}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page.current,
          total: tests?.count,
          pageSize: page.size,
          showSizeChanger: true
        }}
        rowSelection={rowSelection}
        onChange={handleTableChange}
      />
      <Button type="primary" onClick={() => onClose()}>
        Close
      </Button>
    </Drawer>
  );
};

export default TestsDrawer;
